/**
 * Per-mission scoring + statistics accumulator (combat scenarios only).
 *
 * A plain data object owned by {@link App} for the life of one combat run.
 * Gameplay systems report events into it through a lightweight `.stats`
 * reference — WeaponSystem (rounds/missiles), CountermeasureDispenser (flares),
 * EnemyManager (kills), EnemyMissileSystem (evasions). The final engine/damage
 * state is captured from the aircraft at {@link finalize}.
 *
 * {@link computeBreakdown} turns the raw counters into the scored, grouped
 * line-items rendered by MissionEndedScreen. All scoring values live in the
 * tables below — the single source of truth for the score sheet.
 */

// Per-enemy-type scoring + display metadata, keyed by the AssetManager model
// name carried on each enemy's `params.model` (see SimConfig HELICOPTERS /
// PLANES / VEHICLES). `klass` rolls each type up into the ground/aircraft tally.
const ENEMY_TYPES = {
  tunguska: { label: 'Tunguska (SAM)',  klass: 'ground',   points: 1000 },
  mi8:      { label: 'Mi-8 Helicopter', klass: 'aircraft', points: 600 },
  mig21:    { label: 'MiG-21',          klass: 'aircraft', points: 1200 },
  btr80:    { label: 'BTR-80',          klass: 'ground',   points: 300 },
};

// Display order for the kill list + enemy-destruction breakdown.
const ENEMY_ORDER = ['tunguska', 'mi8', 'mig21', 'btr80'];

const SCORING = {
  missionComplete: 5000,
  nightOps: 1000,             // bonus for completing a mission in night conditions
  noHud: 2500,                // bonus for completing a mission with the HUD disabled
  unlimitedAmmo:   -1000,     // penalty for the unlimited 30mm cheat
  unlimitedFlares: -1000,     // penalty for the unlimited flares cheat
  // 30mm accuracy — highest qualifying tier wins (not cumulative).
  gunAccuracyTiers: [
    { minPct: 10, points: 1000 },
    { minPct: 5,  points: 500 },
  ],
  maverick:   { hit: 400, miss: -300 },
  sidewinder: { hit: 500, miss: -300 },
  flares:     { free: 40, penaltyEach: -5 },  // -5 per flare beyond `free`
  missileEvaded:   300,
  noDamage:        1500,
  oneEngineLost:  -1000,
  bothEnginesLost: -2500,
};

// Score → letter grade thresholds (descending). First match wins.
const GRADES = [
  { min: 10000, grade: 'S' },
  { min: 8000,  grade: 'A' },
  { min: 6000,  grade: 'B' },
  { min: 4000,  grade: 'C' },
  { min: 2000,  grade: 'D' },
  { min: -Infinity, grade: 'F' },
];

// One decimal, dropping a trailing ".0" (7.8% but 100%).
function pct(n) {
  const r = Math.round(n * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}%`;
}

export default class MissionStats {
  /**
   * @param {import('../aircraft/Aircraft.js').default} aircraft
   * @param {object} [modifiers] - difficulty modifiers active for this run, scored
   *   under the Difficulty category.
   * @param {boolean} [modifiers.night] - night lighting (NIGHT OPS BONUS).
   * @param {boolean} [modifiers.noHud] - HUD disabled (NO HUD BONUS).
   * @param {boolean} [modifiers.unlimitedAmmo] - unlimited 30mm cheat (penalty).
   * @param {boolean} [modifiers.unlimitedFlares] - unlimited flares cheat (penalty).
   */
  constructor(aircraft, modifiers = {}) {
    this._aircraft = aircraft;
    this.night = !!modifiers.night;
    this.noHud = !!modifiers.noHud;
    this.unlimitedAmmo = !!modifiers.unlimitedAmmo;
    this.unlimitedFlares = !!modifiers.unlimitedFlares;

    // ── Munitions ──
    this.gunFired = 0;
    this.gunHits = 0;
    this.mavericksFired = 0;
    this.maverickHits = 0;
    this.maverickMisses = 0;
    this.sidewindersFired = 0;
    this.sidewinderHits = 0;
    this.sidewinderMisses = 0;
    this.flaresDeployed = 0;

    // ── Survivability ──
    this.missilesEvaded = 0;

    // ── Kills (keyed by enemy model name) ──
    this.kills = { tunguska: 0, mi8: 0, mig21: 0, btr80: 0 };

    this.missionComplete = false;

    // Latched true the instant the mission completes (setMissionComplete). Once
    // locked, all event recording is ignored so resuming free flight over the
    // cleared battlefield can't keep inflating the score sheet.
    this._locked = false;

    // Captured from the aircraft at finalize() so the score reflects the
    // engine/damage state at the moment the mission ended.
    this._finalized = false;
    this.engineLeftOut = false;
    this.engineRightOut = false;
  }

  // ── Event recording (called by gameplay systems via their `.stats` ref) ──
  // Each is a no-op once the mission is complete (see `_locked`).
  recordGunFired()        { if (!this._locked) this.gunFired++; }
  recordGunHit()          { if (!this._locked) this.gunHits++; }
  recordSidewinderFired() { if (!this._locked) this.sidewindersFired++; }
  recordMaverickFired()   { if (!this._locked) this.mavericksFired++; }
  recordFlare()           { if (!this._locked) this.flaresDeployed++; }
  recordMissileEvaded()   { if (!this._locked) this.missilesEvaded++; }
  setMissionComplete()    { this.missionComplete = true; this._locked = true; }

  /** A fired guided missile resolved. kind: 'aim9' | 'agm65'. */
  recordMissileResult(kind, hit) {
    if (this._locked) return;
    if (kind === 'aim9') {
      if (hit) this.sidewinderHits++; else this.sidewinderMisses++;
    } else {
      if (hit) this.maverickHits++; else this.maverickMisses++;
    }
  }

  /** An enemy was destroyed. `model` is its `params.model` (e.g. 'btr80'). */
  recordEnemyKill(model) {
    if (this._locked) return;
    if (model && model in this.kills) this.kills[model]++;
  }

  /** Snapshot the aircraft's final engine state. Idempotent. */
  finalize() {
    if (this._finalized) return;
    this._finalized = true;
    const a = this._aircraft;
    this.engineLeftOut = !!a?.engineLeftOut;
    this.engineRightOut = !!a?.engineRightOut;
  }

  // ── Derived tallies ──
  get totalKills()    { return ENEMY_ORDER.reduce((s, k) => s + this.kills[k], 0); }
  get groundKills()   { return this.kills.tunguska + this.kills.btr80; }
  get aircraftKills() { return this.kills.mi8 + this.kills.mig21; }

  get gunAccuracy()        { return this.gunFired ? (this.gunHits / this.gunFired) * 100 : 0; }
  get maverickAccuracy()   { return this.mavericksFired ? (this.maverickHits / this.mavericksFired) * 100 : 0; }
  get sidewinderAccuracy() { return this.sidewindersFired ? (this.sidewinderHits / this.sidewindersFired) * 100 : 0; }

  /** 'none' | 'one' | 'both' — drives the survivability label + scoring. */
  get damageState() {
    if (this.engineLeftOut && this.engineRightOut) return 'both';
    if (this.engineLeftOut || this.engineRightOut) return 'one';
    return 'none';
  }

  // Pre-formatted accuracy strings for the ACCURACY card ('—' when nothing
  // of that type was fired).
  get accuracyStrings() {
    return {
      gun:        this.gunFired ? pct(this.gunAccuracy) : '—',
      maverick:   this.mavericksFired ? pct(this.maverickAccuracy) : '—',
      sidewinder: this.sidewindersFired ? pct(this.sidewinderAccuracy) : '—',
    };
  }

  /** Per-type kill rows (count > 0 only) for the ENEMY KILLS card. */
  get killRows() {
    return ENEMY_ORDER
      .filter((k) => this.kills[k] > 0)
      .map((k) => ({ label: ENEMY_TYPES[k].label, count: this.kills[k] }));
  }

  static gradeForScore(score) {
    return GRADES.find((g) => score >= g.min).grade;
  }

  /**
   * Rebuild a (finalized, locked) MissionStats from a saved result snapshot so
   * the score sheet can re-render a past run exactly as it was scored. The
   * inverse of {@link toResult}; scoring is deterministic, so computeBreakdown()
   * on the result reproduces the saved score/grade.
   *
   * @param {{ stats: object }} result - a saved MissionResult (see MissionRecords)
   * @returns {MissionStats | null}
   */
  static fromResult(result) {
    const s = result?.stats;
    if (!s) return null;
    const ms = new MissionStats(null, {
      night: s.night,
      noHud: s.noHud,
      unlimitedAmmo: s.unlimitedAmmo,
      unlimitedFlares: s.unlimitedFlares,
    });
    ms.gunFired = s.gunFired || 0;
    ms.gunHits = s.gunHits || 0;
    ms.mavericksFired = s.mavericksFired || 0;
    ms.maverickHits = s.maverickHits || 0;
    ms.maverickMisses = s.maverickMisses || 0;
    ms.sidewindersFired = s.sidewindersFired || 0;
    ms.sidewinderHits = s.sidewinderHits || 0;
    ms.sidewinderMisses = s.sidewinderMisses || 0;
    ms.flaresDeployed = s.flaresDeployed || 0;
    ms.missilesEvaded = s.missilesEvaded || 0;
    ms.kills = { tunguska: 0, mi8: 0, mig21: 0, btr80: 0, ...(s.kills || {}) };
    ms.missionComplete = !!s.missionComplete;
    // Reconstruct the engine-damage flags the `damageState` getter derives from.
    ms.engineLeftOut = s.damageState === 'one' || s.damageState === 'both';
    ms.engineRightOut = s.damageState === 'both';
    // Frozen snapshot: block further event recording and skip aircraft capture.
    ms._locked = true;
    ms._finalized = true;
    return ms;
  }

  /**
   * A compact, serializable snapshot of this run for persistence (see
   * MissionRecords). Carries the final score + grade alongside the raw counters
   * and derived tallies so a saved run can be shown without recomputing.
   *
   * @param {'complete' | 'failed'} outcome
   */
  toResult(outcome) {
    const bd = this.computeBreakdown();
    return {
      score: bd.grand,
      grade: bd.grade,
      outcome,
      stats: {
        gunFired: this.gunFired,
        gunHits: this.gunHits,
        gunAccuracy: this.gunAccuracy,
        mavericksFired: this.mavericksFired,
        maverickHits: this.maverickHits,
        maverickMisses: this.maverickMisses,
        maverickAccuracy: this.maverickAccuracy,
        sidewindersFired: this.sidewindersFired,
        sidewinderHits: this.sidewinderHits,
        sidewinderMisses: this.sidewinderMisses,
        sidewinderAccuracy: this.sidewinderAccuracy,
        flaresDeployed: this.flaresDeployed,
        missilesEvaded: this.missilesEvaded,
        kills: { ...this.kills },
        totalKills: this.totalKills,
        groundKills: this.groundKills,
        aircraftKills: this.aircraftKills,
        damageState: this.damageState,
        missionComplete: this.missionComplete,
        night: this.night,
        noHud: this.noHud,
        unlimitedAmmo: this.unlimitedAmmo,
        unlimitedFlares: this.unlimitedFlares,
      },
    };
  }

  /**
   * Build the scored, grouped breakdown for the score sheet. Returns the four
   * category groups (each a list of { label, value } line-items plus a total),
   * a top-level summary, the grand total, and the letter grade.
   */
  computeBreakdown() {
    const S = SCORING;

    // ── Mission ──
    const mission = this.missionComplete ? S.missionComplete : 0;

    // ── Difficulty ──
    // Challenge bonuses pay out only on a successful completion under their
    // condition; cheat penalties apply whenever the cheat was enabled, win or
    // lose. A line appears only when its modifier was active for the run; the
    // score card shows just the cumulative total.
    const diffLines = [];
    let diffTotal = 0;
    if (this.night) {
      const value = this.missionComplete ? S.nightOps : 0;
      diffLines.push({ label: 'Night Ops', value });
      diffTotal += value;
    }
    if (this.noHud) {
      const value = this.missionComplete ? S.noHud : 0;
      diffLines.push({ label: 'No HUD', value });
      diffTotal += value;
    }
    if (this.unlimitedAmmo) {
      diffLines.push({ label: 'Unlimited 30mm', value: S.unlimitedAmmo });
      diffTotal += S.unlimitedAmmo;
    }
    if (this.unlimitedFlares) {
      diffLines.push({ label: 'Unlimited Flares', value: S.unlimitedFlares });
      diffTotal += S.unlimitedFlares;
    }

    // ── Enemy destruction ──
    const enemyLines = [];
    let enemyTotal = 0;
    for (const key of ENEMY_ORDER) {
      const n = this.kills[key];
      if (n <= 0) continue;
      const value = n * ENEMY_TYPES[key].points;
      enemyTotal += value;
      enemyLines.push({ label: `${ENEMY_TYPES[key].label} × ${n}`, value });
    }

    // ── Weapon efficiency ──
    const weaponLines = [];
    let weaponTotal = 0;
    if (this.gunFired > 0) {
      const tier = S.gunAccuracyTiers.find((t) => this.gunAccuracy >= t.minPct);
      const value = tier ? tier.points : 0;
      weaponLines.push({ label: `30mm Accuracy (${pct(this.gunAccuracy)})`, value });
      weaponTotal += value;
    }
    if (this.maverickHits > 0) {
      const value = this.maverickHits * S.maverick.hit;
      weaponLines.push({ label: `Maverick Hits (${this.maverickHits})`, value });
      weaponTotal += value;
    }
    if (this.maverickMisses > 0) {
      const value = this.maverickMisses * S.maverick.miss;
      weaponLines.push({ label: `Maverick Misses (${this.maverickMisses})`, value });
      weaponTotal += value;
    }
    if (this.sidewinderHits > 0) {
      const value = this.sidewinderHits * S.sidewinder.hit;
      weaponLines.push({ label: `Sidewinder Hits (${this.sidewinderHits})`, value });
      weaponTotal += value;
    }
    if (this.sidewinderMisses > 0) {
      const value = this.sidewinderMisses * S.sidewinder.miss;
      weaponLines.push({ label: `Sidewinder Misses (${this.sidewinderMisses})`, value });
      weaponTotal += value;
    }
    const flareOverflow = Math.max(0, this.flaresDeployed - S.flares.free);
    if (flareOverflow > 0) {
      const value = flareOverflow * S.flares.penaltyEach;
      weaponLines.push({ label: `Flares Deployed (${this.flaresDeployed})`, value });
      weaponTotal += value;
    }

    // ── Survivability ──
    const survLines = [];
    let survTotal = 0;
    if (this.missilesEvaded > 0) {
      const value = this.missilesEvaded * S.missileEvaded;
      survLines.push({ label: `Enemy Missiles Evaded (${this.missilesEvaded})`, value });
      survTotal += value;
    }
    const dmg = this.damageState;
    if (dmg === 'both') {
      survLines.push({ label: 'Both Engines Lost', value: S.bothEnginesLost });
      survTotal += S.bothEnginesLost;
    } else if (dmg === 'one') {
      survLines.push({ label: 'One Engine Lost', value: S.oneEngineLost });
      survTotal += S.oneEngineLost;
    } else {
      survLines.push({ label: 'No Damage Taken Bonus', value: S.noDamage });
      survTotal += S.noDamage;
    }

    const grand = mission + diffTotal + enemyTotal + weaponTotal + survTotal;

    return {
      summary: [
        { label: 'Mission Complete', value: mission },
        { label: 'Difficulty', value: diffTotal },
        { label: 'Enemy Destruction', value: enemyTotal },
        { label: 'Weapon Efficiency', value: weaponTotal },
        { label: 'Survivability', value: survTotal },
      ],
      groups: [
        { title: 'Difficulty', lines: diffLines, total: diffTotal },
        { title: 'Enemy Destruction', lines: enemyLines, total: enemyTotal },
        { title: 'Weapon Efficiency', lines: weaponLines, total: weaponTotal },
        { title: 'Survivability', lines: survLines, total: survTotal },
      ],
      grand,
      grade: MissionStats.gradeForScore(grand),
    };
  }
}
